import { useState } from 'react';
import './MapWithAlerts.css';
import { useTranslation } from 'react-i18next';

import CoordinateSystem from './CoordinateSystem';
import type { Alert } from 'src/types';
import type { CabinetBox } from 'src/utils/visualization';
import { parseUtcTimestamp } from 'src/utils/timestamps';

interface MapWithAlertsProps {
  points: any[];
  // Open alerts from the alert log (the source of truth for displayed alerts).
  // The banner is driven entirely by these — never by per-point threshold flags.
  alerts?: Alert[];
  polygons: any[];
  interpolatedData?: any | null;
  measurementTimestamp?: string | null;
  // When true, a newly-selected snapshot is being fetched; show a loading
  // overlay on the map while the previous frame stays visible underneath.
  loading?: boolean;
  // Optional: enables the admin focal-point picker overlay on the viz.
  onBackgroundClick?: (worldX: number, worldY: number) => void;
  focusPoint?: { x: number; y: number } | null;
  // Optional persistent markers — used to render every active alert's
  // focal point on the live Status viz.
  focusPoints?: Array<{ x: number; y: number; color?: string; title?: string }>;
  // Optional cabinet regions to box on the map (one per cabinet with an active
  // alert). Forwarded to CoordinateSystem.
  cabinetBoxes?: CabinetBox[];
  // Per-installation admin viz settings, forwarded to CoordinateSystem.
  showMple?: boolean;
  showInterpolation?: boolean;
  pointRadius?: number;
  // Forwarded to CoordinateSystem: which value the viz represents, and a live
  // color-domain preview (from the admin color-scale panel).
  mode?: 'voltage' | 'deviation';
  colorOverride?: { min: number; max: number } | null;
}

const MapWithAlerts = ({
  points = [],
  alerts = [],
  polygons = [],
  interpolatedData = null,
  measurementTimestamp = null,
  loading = false,
  onBackgroundClick,
  focusPoint = null,
  focusPoints = [],
  cabinetBoxes = [],
  showMple = true,
  showInterpolation = true,
  pointRadius = 3.5,
  mode = 'voltage',
  colorOverride = null,
}: MapWithAlertsProps) => {
  const { t } = useTranslation();
  const [selectedPoint, setSelectedPoint] = useState<any | null>(null);

  // Naive-UTC backend timestamp — parse as UTC so the local rendering is right.
  const formattedDateTime = parseUtcTimestamp(measurementTimestamp)?.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  const hasAlerts = alerts.length > 0;

  return (
    <div className="map-container">
      {formattedDateTime && (
        <div className="map-timestamp">{t('alert.asOf', { time: formattedDateTime })}</div>
      )}
      {hasAlerts && (
        <div className="alert-banner alert-banner--warning">
          <div className="alert-banner-header">
            <span className="alert-banner-icon">{'\u26A0'}</span>
            <span className="alert-banner-title">
              {t('alert.countActive', { count: alerts.length })}
            </span>
          </div>
          <ul className="alert-banner-list">
            {alerts.map((alert) => (
              <li key={alert.id} className="alert-banner-item-wrapper">
                <div className="alert-banner-item">
                  <span className="alert-banner-item-dot" />
                  {alert.schaltschranknummer
                    ? `${t('status.cabinet', { nr: alert.schaltschranknummer })} \u2014 `
                    : ''}
                  {alert.message || t(`alarm.type.${alert.type}`)}
                  {alert.severity
                    ? ` \u2014 ${t(`management.alert.severity.${alert.severity}`)}`
                    : ''}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="map-viz-wrapper">
        <CoordinateSystem
          points={points}
          polygons={polygons}
          interpolatedData={interpolatedData}
          highlightedPoint={null}
          selectedPoint={selectedPoint}
          onPointSelect={setSelectedPoint}
          onBackgroundClick={onBackgroundClick}
          focusPoint={focusPoint}
          focusPoints={focusPoints}
          cabinetBoxes={cabinetBoxes}
          showMple={showMple}
          showInterpolation={showInterpolation}
          pointRadius={pointRadius}
          mode={mode}
          colorOverride={colorOverride}
        />
        {loading && (
          <div className="map-loading-overlay" role="status" aria-live="polite">
            <div className="map-spinner" />
          </div>
        )}
      </div>
    </div>
  );
};

export default MapWithAlerts;
